/*
  Canwell signup offer.
  Drives <canwell-signup-offer> from sections/canwell-signup-offer.liquid, a
  small non-modal panel offering 10% off a first order for joining the
  mailing list. The code is emailed by Shopify Messaging; nothing here knows
  it.

  When the panel opens:
    on its own    at most once per session, when the reader scrolls past the
                  section's scroll setting or after its delay, whichever comes
                  first. Never on load, never within the dismiss window after
                  a close, never after a signup. Focus does not move.
    on request    a click on a link ending #canwell-signup or on anything with
                  data-canwell-signup-open, or a page opened at
                  #canwell-signup. Focus moves to the email field. Ignores the
                  dismiss window and the once per session rule, and shows the
                  already subscribed message after a signup.
    after a post  in its success state, only when the post came from this
                  panel.

  Browser storage, every read and write wrapped so the panel still works
  when storage is blocked:
    canwell-signup-done          local    1         any successful customer form post
    canwell-signup-dismissed-at  local    epoch ms  closed without signing up
    canwell-signup-auto-shown    session  1         opened on its own
    canwell-signup-pending       session  source    this panel's form submitted

  The source (timed, product, announcement or link) is written to the form's
  tags and data-canwell-source when the panel opens, not on submit, because
  canwell-events.js reads the form on the capture phase of the submit event,
  before any handler on the form runs.

  While Horizon's sticky add-to-basket bar shows, the panel is lifted by the
  bar's height so the bar is never covered.
*/
(function () {
  var KEY_DONE = 'canwell-signup-done';
  var KEY_DISMISSED_AT = 'canwell-signup-dismissed-at';
  var KEY_AUTO_SHOWN = 'canwell-signup-auto-shown';
  var KEY_PENDING = 'canwell-signup-pending';
  var HASH = '#canwell-signup';
  var TRIGGERS = 'a[href$="#canwell-signup"], [data-canwell-signup-open]';
  var SOURCES = ['timed', 'product', 'announcement', 'link'];
  var DAY_MS = 24 * 60 * 60 * 1000;

  function store(type) {
    try {
      return type === 'session' ? window.sessionStorage : window.localStorage;
    } catch (error) {
      return null;
    }
  }

  function read(type, key) {
    try {
      return store(type).getItem(key);
    } catch (error) {
      return null;
    }
  }

  function write(type, key, value) {
    try {
      store(type).setItem(key, value);
    } catch (error) {
      /* storage blocked: carry on without remembering */
    }
  }

  function forget(type, key) {
    try {
      store(type).removeItem(key);
    } catch (error) {
      /* storage blocked: nothing to forget */
    }
  }

  function isDone() {
    return read('local', KEY_DONE) === '1';
  }

  function cleanSource(value) {
    return SOURCES.indexOf(value) === -1 ? 'link' : value;
  }

  /* Horizon scrolls .page-wrapper instead of the window from 990px up
     (assets/scroll-container.js), so take whichever of the two is scrolling.
     A container that cannot scroll counts as 0, so a short page only ever
     opens on the timer. */
  function scrollProgress() {
    var progress = 0;
    var range = document.documentElement.scrollHeight - window.innerHeight;
    if (range > 0) progress = window.scrollY / range;
    var wrapper = document.querySelector('.page-wrapper');
    if (wrapper) {
      var wrapperRange = wrapper.scrollHeight - wrapper.clientHeight;
      if (wrapperRange > 0) progress = Math.max(progress, wrapper.scrollTop / wrapperRange);
    }
    return progress;
  }

  class CanwellSignupOffer extends HTMLElement {
    constructor() {
      super();
      this.onDocumentClick = this.onDocumentClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onHashChange = this.onHashChange.bind(this);
      this.onSubmit = this.onSubmit.bind(this);
      this.onScroll = this.onScroll.bind(this);
      this.onTimer = this.onTimer.bind(this);
      this.onSectionSelect = this.onSectionSelect.bind(this);
      this.onSectionDeselect = this.onSectionDeselect.bind(this);
      this.close = this.close.bind(this);
      this.updateLift = this.updateLift.bind(this);
    }

    connectedCallback() {
      this.panel = this.querySelector('[data-signup-panel]');
      this.form = this.querySelector('form');
      if (!this.panel || !this.form) return;

      this.email = this.form.querySelector('input[type="email"]');
      this.tags = this.form.querySelector('input[name="contact[tags]"]');
      this.closeButton = this.querySelector('[data-signup-close]');
      this.baseTags = this.tags ? this.tags.value.replace(/,\s*signup-[a-z]+$/, '') : '';
      this.scrollPercent = Number(this.dataset.scrollPercent) || 50;
      this.delaySeconds = Number(this.dataset.delaySeconds) || 20;
      this.dismissDays = Number(this.dataset.dismissDays) || 30;
      this.designMode = Boolean(window.Shopify && window.Shopify.designMode);
      this.source = 'link';
      this.state = 'form';
      this.returnFocus = null;
      this.timer = null;
      this.scrollTargets = [];

      if (!this.form.hasAttribute('data-canwell-form')) {
        this.form.setAttribute('data-canwell-form', 'newsletter');
      }

      this.form.addEventListener('submit', this.onSubmit);
      if (this.closeButton) this.closeButton.addEventListener('click', this.close);
      document.addEventListener('click', this.onDocumentClick, true);
      document.addEventListener('keydown', this.onKeydown);
      window.addEventListener('hashchange', this.onHashChange);
      if (this.designMode) {
        document.addEventListener('shopify:section:select', this.onSectionSelect);
        document.addEventListener('shopify:section:deselect', this.onSectionDeselect);
      }

      this.watchStickyBar();
      this.start();
    }

    disconnectedCallback() {
      this.disarm();
      document.removeEventListener('click', this.onDocumentClick, true);
      document.removeEventListener('keydown', this.onKeydown);
      window.removeEventListener('hashchange', this.onHashChange);
      window.removeEventListener('resize', this.updateLift);
      document.removeEventListener('shopify:section:select', this.onSectionSelect);
      document.removeEventListener('shopify:section:deselect', this.onSectionDeselect);
      if (this.barObserver) this.barObserver.disconnect();
      if (this.barResizeObserver) this.barResizeObserver.disconnect();
    }

    start() {
      var params = new URLSearchParams(window.location.search);
      var posted = params.get('customer_posted') === 'true' || this.dataset.posted === 'true';
      var pending = read('session', KEY_PENDING);

      /* 1. After a post. Any customer form counts as a signup, but only a post
         from this panel opens it. If the panel's own post came back with an
         error, reopen it so the error can be read. */
      if (posted) {
        write('local', KEY_DONE, '1');
        if (pending) {
          forget('session', KEY_PENDING);
          this.open(cleanSource(pending), { state: 'success' });
        }
      } else if (pending && this.dataset.errors === 'true') {
        forget('session', KEY_PENDING);
        this.open(cleanSource(pending), { state: 'form', focus: true });
      }

      var done = isDone();

      /* 2. The offer line on product pages stays hidden until here, so it
         never shows to anyone already signed up. */
      if (!done) {
        document.querySelectorAll('.canwell-signup-line').forEach(function (line) {
          line.hidden = false;
        });
      }

      /* 3. Direct link */
      if (window.location.hash === HASH && this.panel.hidden) {
        this.open('link', { focus: true });
      }

      /* 4. On its own */
      if (this.panel.hidden && this.canAutoOpen()) this.arm();
    }

    canAutoOpen() {
      if (this.designMode || isDone()) return false;
      if (read('session', KEY_AUTO_SHOWN) === '1') return false;
      /* Not over Shopify's bot challenge after a signup */
      if (window.location.pathname.indexOf('/challenge') === 0) return false;
      var dismissedAt = parseInt(read('local', KEY_DISMISSED_AT), 10);
      if (dismissedAt && Date.now() - dismissedAt < this.dismissDays * DAY_MS) return false;
      return true;
    }

    arm() {
      this.timer = window.setTimeout(this.onTimer, this.delaySeconds * 1000);
      this.scrollTargets = [window];
      var wrapper = document.querySelector('.page-wrapper');
      if (wrapper) this.scrollTargets.push(wrapper);
      this.scrollTargets.forEach(function (target) {
        target.addEventListener('scroll', this.onScroll, { passive: true });
      }, this);
    }

    disarm() {
      if (this.timer) {
        window.clearTimeout(this.timer);
        this.timer = null;
      }
      this.scrollTargets.forEach(function (target) {
        target.removeEventListener('scroll', this.onScroll);
      }, this);
      this.scrollTargets = [];
    }

    onScroll() {
      if (scrollProgress() >= this.scrollPercent / 100) this.autoOpen();
    }

    onTimer() {
      this.timer = null;
      this.autoOpen();
    }

    autoOpen() {
      if (!this.panel.hidden || !this.canAutoOpen()) {
        this.disarm();
        return;
      }
      this.open('timed', { auto: true });
    }

    open(source, options) {
      var settings = options || {};
      this.disarm();
      this.setSource(source);
      this.setState(settings.state || (isDone() ? 'already' : 'form'));
      this.returnFocus = settings.trigger || null;
      this.panel.hidden = false;
      this.updateLift();
      if (settings.auto) write('session', KEY_AUTO_SHOWN, '1');
      if (settings.focus) {
        var target = this.state === 'form' && this.email ? this.email : this.closeButton;
        if (target) target.focus({ preventScroll: true });
      }
    }

    close() {
      if (this.panel.hidden) return;
      this.panel.hidden = true;
      if (this.state === 'form') write('local', KEY_DISMISSED_AT, String(Date.now()));
      var trigger = this.returnFocus;
      this.returnFocus = null;
      if (trigger && trigger.isConnected && typeof trigger.focus === 'function') {
        trigger.focus({ preventScroll: true });
      }
    }

    setSource(source) {
      this.source = source;
      if (this.tags) this.tags.value = this.baseTags + ', signup-' + source;
      this.form.setAttribute('data-canwell-source', source);
    }

    setState(state) {
      this.state = state;
      this.querySelectorAll('[data-signup-state]').forEach(function (view) {
        view.hidden = view.getAttribute('data-signup-state') !== state;
      });
    }

    onSubmit() {
      write('session', KEY_PENDING, this.source);
    }

    onDocumentClick(event) {
      var trigger = event.target instanceof Element ? event.target.closest(TRIGGERS) : null;
      if (!trigger) return;
      /* Let a modified click on a link open a new tab, where the hash opens
         the panel. */
      if (trigger.matches('a') && (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0)) return;
      event.preventDefault();
      var source = trigger.getAttribute('data-canwell-source');
      /* Horizon's announcement block takes only a text and a link setting,
         so a link in the utility strip is known by where it sits. */
      if (!source && trigger.closest('.announcement-bar')) source = 'announcement';
      this.open(cleanSource(source), { focus: true, trigger: trigger });
    }

    onHashChange() {
      if (window.location.hash === HASH) this.open('link', { focus: true });
    }

    onKeydown(event) {
      if (event.key !== 'Escape' || this.panel.hidden) return;
      /* Escape inside an open drawer or modal belongs to that dialog. */
      if (event.target instanceof Element && event.target.closest('dialog[open]')) return;
      this.close();
    }

    /* Theme editor: show the panel while its section is selected, without
       touching storage, so the copy can be edited in place. */
    onSectionSelect(event) {
      if (!(event.target instanceof Element) || !event.target.contains(this)) return;
      this.setSource('link');
      this.setState('form');
      this.panel.hidden = false;
      this.updateLift();
    }

    onSectionDeselect(event) {
      if (!(event.target instanceof Element) || !event.target.contains(this)) return;
      this.panel.hidden = true;
    }

    watchStickyBar() {
      this.stickyBar = document.querySelector('.sticky-add-to-cart__bar');
      if (!this.stickyBar) return;
      this.barObserver = new MutationObserver(this.updateLift);
      this.barObserver.observe(this.stickyBar, { attributes: true, attributeFilter: ['data-stuck'] });
      var host = this.stickyBar.closest('sticky-add-to-cart');
      if (host) this.barObserver.observe(host, { attributes: true, attributeFilter: ['data-variant-available'] });
      if ('ResizeObserver' in window) {
        this.barResizeObserver = new ResizeObserver(this.updateLift);
        this.barResizeObserver.observe(this.stickyBar);
      }
      window.addEventListener('resize', this.updateLift);
    }

    /* Height plus bottom offset, read without transforms so the bar's slide
       in does not skew it, plus the 1 or 2px border Horizon draws outside
       the bar with ::before. offsetHeight is 0 while the bar is display: none
       (variant unavailable). */
    updateLift() {
      if (!this.panel) return;
      var lift = 0;
      var bar = this.stickyBar;
      if (bar && bar.getAttribute('data-stuck') === 'true' && bar.offsetHeight > 0) {
        var outside = -parseFloat(window.getComputedStyle(bar, '::before').top) || 0;
        lift = bar.offsetHeight + (parseFloat(window.getComputedStyle(bar).bottom) || 0) + Math.max(0, outside);
      }
      this.style.setProperty('--canwell-signup-lift', lift + 'px');
      this.panel.toggleAttribute('data-lifted', lift > 0);
    }
  }

  if (!customElements.get('canwell-signup-offer')) {
    customElements.define('canwell-signup-offer', CanwellSignupOffer);
  }
})();
